/**
 * Disponibilità in negozio.
 *
 * Popola il popup <store-availability> della pagina prodotto con le giacenze
 * per punto vendita lette dall'intranet (/webhooks/shopify/stock/availability).
 * Le varianti Shopify vengono agganciate ai pieces dell'intranet per barcode
 * o, in mancanza, per sku; il token è quello pubblico di window.loyaltyConfig.
 *
 * La chiamata parte solo alla prima apertura del popup (nessun costo sulle
 * visite che non lo usano) e la risposta resta in sessionStorage per qualche
 * minuto. Il markup del popup è un <modal-dialog> del tema: apertura e
 * chiusura le gestisce modal-dialog.js, qui si gestisce solo il contenuto.
 */
(function () {
  if (customElements.get('store-availability')) {
    return;
  }

  const CACHE_TTL = 5 * 60 * 1000; // 5 minuti

  class StoreAvailability extends HTMLElement {
    connectedCallback() {
      this.state = 'idle';
      this.stockData = null;
      this.selectedId = parseInt(this.dataset.selectedVariant, 10) || null;

      try {
        this.variants = JSON.parse(this.querySelector('[data-store-availability-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }

      // Senza barcode né sku non c'è modo di interrogare l'intranet.
      if (!this.variants.some((variant) => variant.barcode || variant.sku)) {
        this.style.display = 'none';
        return;
      }

      const opener = this.querySelector('modal-opener button');
      if (opener) {
        opener.addEventListener('click', () => this.load());
      }

      this.onVariantChange = (event) => {
        if (!event.detail || !event.detail.variant || event.detail.sectionId !== this.dataset.section) {
          return;
        }
        this.selectedId = event.detail.variant.id;
        this.highlight();
      };
      document.addEventListener('product:variant-change', this.onVariantChange);
    }

    disconnectedCallback() {
      if (this.onVariantChange) {
        document.removeEventListener('product:variant-change', this.onVariantChange);
      }
    }

    // Il modal-dialog si sposta da solo dentro <body>, quindi il contenuto
    // va sempre cercato a partire dal suo id, non da questo elemento.
    content() {
      const modal = document.getElementById(`StoreAvailability-${this.dataset.section}`);
      return modal && modal.querySelector('[data-store-availability-content]');
    }

    cacheKey() {
      return `storeStock:${this.dataset.productId}`;
    }

    readCache() {
      try {
        const cached = JSON.parse(sessionStorage.getItem(this.cacheKey()));
        if (!cached || Date.now() - cached.ts > CACHE_TTL) {
          return null;
        }
        return cached.data;
      } catch (e) {
        return null;
      }
    }

    writeCache(data) {
      try {
        sessionStorage.setItem(this.cacheKey(), JSON.stringify({ data, ts: Date.now() }));
      } catch (e) {
        // Storage pieno o disabilitato: pazienza, si rifà la chiamata.
      }
    }

    load() {
      if (this.state === 'loading' || this.state === 'done') {
        return;
      }

      const cached = this.readCache();
      if (cached) {
        this.state = 'done';
        this.render(cached);
        return;
      }

      const config = window.loyaltyConfig;
      if (!config || !config.apiToken) {
        this.showStatus('Impossibile recuperare la disponibilità. Riprova più tardi.');
        return;
      }

      this.state = 'loading';
      this.showStatus('Caricamento disponibilità…');

      const body = new URLSearchParams();
      body.append('api_key', config.apiToken);
      this.variants.forEach((variant) => {
        if (variant.barcode) {
          body.append('barcodes[]', variant.barcode);
        }
        if (variant.sku) {
          body.append('skus[]', variant.sku);
        }
      });

      fetch(this.dataset.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      })
        .then((res) => res.json())
        .then((json) => {
          if (!json.success || !json.data) {
            throw new Error('Risposta non valida');
          }
          this.state = 'done';
          this.writeCache(json.data);
          this.render(json.data);
        })
        .catch(() => {
          // Riprovabile: alla prossima apertura si ritenta la chiamata.
          this.state = 'idle';
          this.showStatus('Impossibile recuperare la disponibilità. Riprova più tardi.');
        });
    }

    showStatus(text) {
      const content = this.content();
      if (!content) {
        return;
      }
      content.innerHTML = '';
      const status = document.createElement('p');
      status.className = 'store-availability--status';
      status.textContent = text;
      content.appendChild(status);
    }

    itemFor(variant, items) {
      return items.find((item) =>
        (variant.barcode && item.barcode === variant.barcode)
        || (variant.sku && item.sku === variant.sku));
    }

    render(data) {
      this.stockData = data;

      const content = this.content();
      if (!content) {
        return;
      }

      if (!data.stores || !data.stores.length) {
        this.showStatus('Al momento questo articolo non è disponibile nei nostri negozi.');
        return;
      }

      const table = document.createElement('table');
      table.className = 'store-availability--table';

      const headRow = document.createElement('tr');
      const storeHead = document.createElement('th');
      storeHead.textContent = 'Negozio';
      headRow.appendChild(storeHead);
      this.variants.forEach((variant) => {
        const th = document.createElement('th');
        th.dataset.variantColumn = variant.id;
        th.textContent = variant.title;
        headRow.appendChild(th);
      });
      table.createTHead().appendChild(headRow);

      const tbody = table.createTBody();
      data.stores.forEach((store) => {
        const row = document.createElement('tr');

        const storeCell = document.createElement('td');
        const name = document.createElement('div');
        name.className = 'store-availability--store';
        name.textContent = store.name;
        storeCell.appendChild(name);
        if (store.city && store.city !== store.name) {
          const city = document.createElement('div');
          city.className = 'store-availability--city';
          city.textContent = store.city;
          storeCell.appendChild(city);
        }
        row.appendChild(storeCell);

        this.variants.forEach((variant) => {
          const item = this.itemFor(variant, data.items || []);
          const quantity = item && item.quantities ? item.quantities[store.code] : null;
          const cell = document.createElement('td');
          cell.dataset.variantColumn = variant.id;
          cell.className = quantity ? 'store-availability--qty' : 'store-availability--none';
          cell.textContent = quantity || '—';
          row.appendChild(cell);
        });

        tbody.appendChild(row);
      });

      const scroll = document.createElement('div');
      scroll.className = 'store-availability--scroll';
      scroll.appendChild(table);

      const note = document.createElement('p');
      note.className = 'store-availability--note';
      note.textContent = 'Le quantità sono indicative e potrebbero variare in negozio.';

      content.innerHTML = '';
      content.appendChild(scroll);
      content.appendChild(note);

      this.highlight();
    }

    // Evidenzia la colonna della taglia selezionata nel form prodotto.
    highlight() {
      const modal = document.getElementById(`StoreAvailability-${this.dataset.section}`);
      if (!modal || !this.selectedId) {
        return;
      }
      modal.querySelectorAll('[data-variant-column]').forEach((cell) => {
        cell.classList.toggle('is-selected', cell.dataset.variantColumn === String(this.selectedId));
      });
    }
  }

  customElements.define('store-availability', StoreAvailability);
})();
